/**
 * 上传进度的唯一推导规则。
 *
 * 后端契约：`upload_status === 'done'` ⟹ `upload_percent == 100`，且 100 只能由
 * upload-complete 写入（分片代理进度上限 99，见 bsp_back/apps/storage/progress.py）。
 * 展示层只负责渲染这条事实，不允许每个列表/详情各自写一套推导式。
 */

export interface UploadPercentFile {
  upload_status?: string | null;
  upload_percent?: number | null;
}

export interface UploadPercentEntry {
  percent?: number | null;
  status?: string | null;
}

/** 状态优先级：本 Tab 的实时 store → 服务端持久化状态。 */
export const resolveUploadStatus = (
  file: UploadPercentFile | null | undefined,
  entry: UploadPercentEntry | null | undefined,
): string => entry?.status ?? file?.upload_status ?? 'uploading';

export const clampUploadPercent = (value: number | null | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
};

/**
 * 解析用于 Progress 组件的百分比。
 *
 * - 完成态恒为 100（服务端 done 是权威事实，不被 store 里的陈旧进度拖回小值）；
 * - 上传中优先 store 实时值，其次服务端持久化值（跨会话/刷新后 store 为空）；
 * - 其余状态兜底 0，并统一 clamp 到 0–100。
 */
export const resolveUploadPercent = (
  file: UploadPercentFile | null | undefined,
  entry: UploadPercentEntry | null | undefined,
): number => {
  const status = resolveUploadStatus(file, entry);
  if (status === 'done' || file?.upload_status === 'done') return 100;

  const percent = entry?.percent ?? file?.upload_percent ?? 0;
  return clampUploadPercent(percent);
};
